import React, { useMemo } from "react";
import { useAuth } from "../../services/AuthContext";

/**
 * Top-of-page trial banner.
 *
 *   • Hidden when the tenant has an active subscription (or none at all).
 *   • Warning (amber) when trialing with <= 7 days remaining.
 *   • Blocked (red) when trial expired, past_due, or cancelled —
 *     workspace writes are already server-side blocked by
 *     `CanAccessWorkspace`, this banner just explains why.
 *
 * Admin sees a "pay now" CTA that dispatches the existing
 * `billing:open-setup` event — `BillingSetupGate` in Main.jsx picks it
 * up and opens `BillingSetupModal` which drives the Stripe Checkout
 * flow. Non-admins see a static hint to contact their admin.
 */
export default function TrialBanner() {
  const auth = useAuth();
  const user = auth?.user;

  const state = useMemo(() => {
    const sub = user?.global_nav?.subscription;
    if (!sub) return null;

    const isAdmin = Array.isArray(user?.memberships)
      ? user.memberships.some((m) => m?.is_owner_or_admin)
      : false;

    const blocked =
      sub.trial_expired === true ||
      sub.status === "past_due" ||
      sub.status === "cancelled";

    if (blocked) return { kind: "blocked", isAdmin };

    if (
      sub.is_trialing &&
      typeof sub.trial_days_remaining === "number" &&
      sub.trial_days_remaining <= 7
    ) {
      return {
        kind: "warning",
        daysLeft: sub.trial_days_remaining,
        isAdmin,
      };
    }

    return null;
  }, [user]);

  if (!state) return null;

  function openBillingSetup() {
    window.dispatchEvent(
      new CustomEvent("billing:open-setup", { detail: { reason: "missing" } })
    );
  }

  if (state.kind === "blocked") {
    return (
      <div className="trial-banner trial-banner--blocked" role="alert">
        <span className="trial-banner__text">
          Trial har gått ut — workspaces är i läsläge tills ett betalkort
          registrerats.
        </span>
        {state.isAdmin ? (
          <button
            type="button"
            className="trial-banner__cta"
            onClick={openBillingSetup}
          >
            Betala för att fortsätta
          </button>
        ) : (
          <span className="trial-banner__hint">
            Kontakta din administratör för att fortsätta.
          </span>
        )}
      </div>
    );
  }

  const dayWord = state.daysLeft === 1 ? "dag" : "dagar";
  return (
    <div className="trial-banner trial-banner--warning" role="status">
      <span className="trial-banner__text">
        Trial går ut om {state.daysLeft} {dayWord}.
      </span>
      {state.isAdmin && (
        <button
          type="button"
          className="trial-banner__cta"
          onClick={openBillingSetup}
        >
          Lägg till betalkort
        </button>
      )}
    </div>
  );
}
